import React from 'react';
import { Progress } from 'antd';
import cx from 'classnames';
import moment from 'moment';
import { colors } from '../../../../styles/variables';
import { AccessTypes } from '../../../common/domain/accountAccessType';
import { AccountStates } from '../../../../slices/accounts/account';
import { CadenceTypesValues } from '../../../auth/proRegister/subscriptionTypes';
import { accountTypeNames } from './constants';
import { Features, isFeatureEnabled } from '../../../../featureToggles/features';

import styles from './currentPlan.module.scss';

const isTrialOfTiersEnabled = isFeatureEnabled(Features.TrialsOfTiers);

interface CurrentPlanProps {
  usage?: number | null;
  responsesAllowed?: number;
  accountStatus?: AccountStates;
  // use for Features.PlgExternal2
  responsesAllowedBeforeLapsed?: number;
  // end of use for Features.PlgExternal2
}

interface LitePlanProps extends CurrentPlanProps {
  accountType: AccessTypes.Lite;
}

interface ProPlanProps extends CurrentPlanProps {
  accountType: AccessTypes.Professional;
  renewalDate?: Date;
  cancellationInProgress?: boolean;
  cadence?: CadenceTypesValues;
}

export class CurrentPlan extends React.Component<LitePlanProps | ProPlanProps> {
  private renderLiteDescription() {
    const { accountType } = this.props;
    if (accountType === AccessTypes.Professional) {
      return null;
    }
    return <div className={styles.liteDescription}>
      Upgrade your plan to unlock<br />more survey responses.
    </div>;
  }

  private getCurrentResponse() {
    const { usage } = this.props;
    return !usage || usage < 0 ? 0 : usage;
  }

  private getPercentage() {
    const { responsesAllowed = 0, responsesAllowedBeforeLapsed = 0, accountStatus } = this.props;
    const currentResponses = this.getCurrentResponse();
    if (!isFeatureEnabled(Features.PlgExternal2)) {
      return currentResponses * 100 / (responsesAllowed || 1);
    }

    const allowed = AccountStates.LAPSED === accountStatus
      ? responsesAllowedBeforeLapsed
      : responsesAllowed;
    return currentResponses * 100 / (allowed || 1);
  }

  private renderSubscriptionEndDate() {
    if (this.props.accountType !== AccessTypes.Professional) {
      return null;
    }

    const { cancellationInProgress, renewalDate, accountStatus } = this.props;
    const isNotActive = accountStatus && [AccountStates.LAPSED, AccountStates.DISABLED].includes(accountStatus);

    if (isNotActive) {
      return null;
    }
    return <div className={cx(styles.description, { [styles.canceled]: cancellationInProgress })}>
      {cancellationInProgress ? 'Subscription ends' : 'Renews'} {moment(renewalDate).format('MMMM DD, YYYY')}
    </div>;
  }

  private renderProgress() {
    const { responsesAllowed = 0, accountType } = this.props;
    const currentResponses = this.getCurrentResponse();
    const percentage = this.getPercentage();
    let description = <>of {responsesAllowed}<br />responses used</>;
    if (accountType === AccessTypes.Professional) {
      if (percentage >= 100) {
        description = <>You’ve reached the maximum amount of responses</>;
      } else {
        description = <>{currentResponses} response{currentResponses === 1 ? '' : 's'}<br />used</>;
      }
    }

    return <div className={styles.progressContainer}>
      <Progress
        type="circle"
        percent={percentage}
        strokeWidth={10}
        width={148}
        showInfo={false}
        strokeColor={percentage < 100 ? colors.sightxPurple400 : colors.teal400}
        trailColor={colors.gray200}
      />
      <div className={styles.centerInfo}>
        <div className={styles.usage}>{Math.round(percentage)}%</div>
        <div className={styles.description}>{description}</div>
      </div>
    </div>;

  }

  private renderDescription() {
    const { accountType, responsesAllowed } = this.props;
    if (accountType === AccessTypes.Professional) {
      const { cadence } = this.props;
      const cadenceText = cadence === CadenceTypesValues.ANNUALLY ? 'Annual' : 'Monthly';
      return <>
        <div className={styles.description}>{cadenceText} subscription  |  {responsesAllowed} responses</div>
        {this.renderSubscriptionEndDate()}
      </>;

    }
    return isTrialOfTiersEnabled ? null : <div className={styles.description}>Single-user</div>;

  }

  render(): JSX.Element {
    const { accountType } = this.props;
    return (
      <div className={styles.container}>
        <span className={styles.badge}>Current plan</span>
        <div className={cx(styles.accountType, { [styles.withPadding]: accountType !== AccessTypes.Professional })}>{accountTypeNames[accountType]}</div>
        {this.renderDescription()}
        {this.renderProgress()}
        {this.renderLiteDescription()}
      </div>
    );
  }
}
